/**
 * Workflows (M26, DEC-083 / DEC-085 / DEC-086): triggers, step types, condition fields and limits. Mirrors backend
 * `app/core/constants/workflows.py` and `app/services/workflows/definition.py`.
 */
import type { PlanKey } from "./plans";
import { CUSTOMER_CONDITION_FIELDS } from "./workflows-customer";
import { AFTER_SALES_STEP_TYPES, COMMERCE_BACKGROUND_STEP_TYPES, COMMERCE_FACING_STEP_TYPES, SERVICE_BACKGROUND_STEP_TYPES, SERVICE_FACING_STEP_TYPES } from "./workflow-service";
import { WORKFLOW_P6_STEP_TYPES } from "./workflows-p6";

export const WORKFLOW_TRIGGERS = [
  "widget_opened",
  "home_option",
  "visitor_first_message",
  "visitor_message",
  "assistant_replied",
  "assistant_could_not_answer",
  "intent_detected",
  "staff_replied",
  "feedback_negative",
  "handoff_requested",
  "conversation_assigned",
  "conversation_status_changed",
  "lead_created",
  "lead_updated",
  "booking_requested",
  "booking_confirmed",
  "booking_declined",
  "booking_cancelled",
  "action_run_finished",
  "source_processed",
  "source_failed",
  "visitor_unresponsive",
  "staff_unresponsive",
  "note_added",
  "before_sla_breach",
  "sla_breached",
  "visitor_visits_page",
  "visitor_event",
  "conversation_rated",
  "reusable",
  "attribute_changed",
  "visitor_clicks_element",
  "platform_event",
  "handoff_reminder_due",
  "handoff_escalation_due",
  "handoff_waiting_digest_due",
  "conversation_returned_to_queue",
  "lead_followed_up",
  "gap_assigned",
  /** Connectors platform (M27 SC, CONN-033): an inbound hook delivered an event from the client's own system. */
  "connector_event_received",
  /** Commerce (M27 S2, scope 14.1 ST19-ST24): an order or quote moment. */
  "order_placed",
  "order_status_changed",
  "order_approval_requested",
  "order_approval_decided",
  "order_sync_failed",
  "quote_status_changed",
  /** After-sales (M27 S3, scope 14.1 ST25-ST31): a visit, a reminder, maintenance, a warranty, a contract, an RMA. */
  "visit_scheduled",
  "visit_status_changed",
  "visit_reminder_due",
  "asset_maintenance_due",
  "asset_warranty_expiring",
  "contract_expiring",
  "rma_status_changed",
  "ticket_created",
  "ticket_updated",
  "ticket_state_changed",
  "ticket_assigned",
  "ticket_returned_to_queue",
  "ticket_customer_replied",
  "ticket_staff_replied",
  "ticket_note_added",
  "ticket_sla_warning",
  "ticket_sla_breached",
  "ticket_unassigned_due",
  "ticket_waiting_reminder_due",
  "ticket_pending_expired",
  "ticket_resolved",
  "ticket_reopened",
  "ticket_auto_close_due",
  "ticket_csat_received",
  "ticket_intake_requested",
] as const;
export type WorkflowTrigger = (typeof WORKFLOW_TRIGGERS)[number];

/** Trigger groups of the list page and the trigger picker. */
export const WORKFLOW_TRIGGER_GROUPS = {
  start: ["widget_opened", "home_option", "visitor_first_message", "visitor_visits_page", "visitor_clicks_element"],
  during: ["visitor_message", "intent_detected", "assistant_replied", "assistant_could_not_answer", "staff_replied", "feedback_negative", "visitor_unresponsive", "visitor_event"],
  staff: [
    "handoff_requested",
    "conversation_assigned",
    "conversation_status_changed",
    "note_added",
    "conversation_rated",
    "staff_unresponsive",
    "before_sla_breach",
    "sla_breached",
    "handoff_reminder_due",
    "handoff_escalation_due",
    "handoff_waiting_digest_due",
    "conversation_returned_to_queue",
  ],
  leads: ["lead_created", "lead_updated", "booking_requested", "booking_confirmed", "booking_declined", "booking_cancelled", "action_run_finished", "lead_followed_up"],
  data: ["attribute_changed"],
  other: ["source_processed", "source_failed", "gap_assigned", "connector_event_received", "reusable"],
  /** Service desk (M27): the ticket moments, in the order of the scope. */
  service: [
    "ticket_created",
    "ticket_updated",
    "ticket_state_changed",
    "ticket_assigned",
    "ticket_returned_to_queue",
    "ticket_customer_replied",
    "ticket_staff_replied",
    "ticket_note_added",
    "ticket_sla_warning",
    "ticket_sla_breached",
    "ticket_unassigned_due",
    "ticket_waiting_reminder_due",
    "ticket_pending_expired",
    "ticket_resolved",
    "ticket_reopened",
    "ticket_auto_close_due",
    "ticket_csat_received",
    "ticket_intake_requested",
  ],
  /** Orders and quotes (M27 S2): the commerce moments, in the order of the scope. */
  orders: ["order_placed", "order_status_changed", "order_approval_requested", "order_approval_decided", "order_sync_failed", "quote_status_changed"],
  /** Field service (M27 S3): visits, assets, contracts and RMAs, in the order of the scope. */
  field_service: ["visit_scheduled", "visit_status_changed", "visit_reminder_due", "asset_maintenance_due", "asset_warranty_expiring", "contract_expiring", "rma_status_changed"],
} as const satisfies Record<string, readonly WorkflowTrigger[]>;
export type WorkflowTriggerGroup = keyof typeof WORKFLOW_TRIGGER_GROUPS;

/** Triggers the picker does not offer: `platform_event` belongs to the Developer webhooks default and to a copy of it (P6). */
export const PICKER_HIDDEN_TRIGGERS: ReadonlySet<WorkflowTrigger> = new Set(["platform_event"]);

/** Triggers that never have a conversation (conversation steps are refused). */
export const NON_CONVERSATION_TRIGGERS: ReadonlySet<WorkflowTrigger> = new Set([
  "source_processed",
  "source_failed",
  "visitor_event",
  "gap_assigned",
  "platform_event",
  "connector_event_received",
  "order_placed",
  "order_status_changed",
  "order_approval_requested",
  "order_approval_decided",
  "order_sync_failed",
  "quote_status_changed",
  "visit_scheduled",
  "visit_status_changed",
  "visit_reminder_due",
  "asset_maintenance_due",
  "asset_warranty_expiring",
  "contract_expiring",
  "rma_status_changed",
  "ticket_created",
  "ticket_updated",
  "ticket_state_changed",
  "ticket_assigned",
  "ticket_returned_to_queue",
  "ticket_customer_replied",
  "ticket_staff_replied",
  "ticket_note_added",
  "ticket_sla_warning",
  "ticket_sla_breached",
  "ticket_unassigned_due",
  "ticket_waiting_reminder_due",
  "ticket_pending_expired",
  "ticket_resolved",
  "ticket_reopened",
  "ticket_auto_close_due",
  "ticket_csat_received",
]);

/** Facing triggers that fire before a conversation exists: a Wait step cannot hold them. */
export const UNATTACHED_TRIGGERS: ReadonlySet<WorkflowTrigger> = new Set(["widget_opened", "home_option", "visitor_visits_page", "visitor_clicks_element"]);

/** Triggers that may host customer-facing workflows (the visitor sees and answers their steps). */
export const FACING_TRIGGERS = [
  "widget_opened",
  "home_option",
  "visitor_first_message",
  "assistant_could_not_answer",
  "intent_detected",
  "visitor_unresponsive",
  "visitor_visits_page",
  "conversation_status_changed",
  "visitor_clicks_element",
  "ticket_intake_requested",
] as const satisfies readonly WorkflowTrigger[];
export type FacingTrigger = (typeof FACING_TRIGGERS)[number];
/** Facing triggers that never host a background workflow. */
export const FACING_ONLY_TRIGGERS: ReadonlySet<string> = new Set(["widget_opened", "home_option", "intent_detected", "visitor_visits_page", "visitor_clicks_element", "ticket_intake_requested"]);

/** The plan that first has Workflows (backend `PlanFeature.WORKFLOWS`: Standard and up). */
export const WORKFLOWS_MIN_PLAN: PlanKey = "standard";

/** Plan a trigger needs (P3); triggers not listed are available on every plan that has workflows. */
export const WORKFLOW_TRIGGER_MIN_PLAN: Readonly<Partial<Record<WorkflowTrigger, PlanKey>>> = {
  visitor_visits_page: "pro",
  staff_unresponsive: "pro",
  before_sla_breach: "institution",
  sla_breached: "institution",
  reusable: "pro",
  visitor_clicks_element: "pro",
  connector_event_received: "pro",
  order_placed: "pro",
  order_status_changed: "pro",
  order_approval_requested: "pro",
  order_approval_decided: "pro",
  order_sync_failed: "pro",
  quote_status_changed: "pro",
  visit_scheduled: "pro",
  visit_status_changed: "pro",
  visit_reminder_due: "pro",
  asset_maintenance_due: "pro",
  asset_warranty_expiring: "pro",
  contract_expiring: "pro",
  rma_status_changed: "pro",
};
/** Plan a step type needs (P3 SLA, P4 reusable workflows). */
export const WORKFLOW_STEP_MIN_PLAN: Readonly<Partial<Record<WorkflowStepType, PlanKey>>> = { apply_sla: "institution", run_workflow: "pro", http_request: "pro", add_summary_note: "pro", apply_ticket_sla: "pro", lookup_asset: "pro", schedule_visit: "pro", lookup_catalogue: "pro", check_order_status: "pro", show_catalogue: "pro", create_order: "pro", show_order_status: "pro" };
const FACING_TRIGGER_SET: ReadonlySet<string> = new Set(FACING_TRIGGERS);
export const isFacingTrigger = (trigger: string): boolean => FACING_TRIGGER_SET.has(trigger);

/** A workflow is customer-facing when it has any step the visitor sees (the server computes `kind` on save). */
export const WORKFLOW_KINDS = ["background", "facing"] as const;
export type WorkflowKind = (typeof WORKFLOW_KINDS)[number];

/** `workflow_runs.status` (phase 2 adds waiting, delegated and cancelled). */
export const WORKFLOW_RUN_STATUSES = ["running", "waiting", "delegated", "completed", "failed", "cancelled"] as const;
export type WorkflowRunStatus = (typeof WORKFLOW_RUN_STATUSES)[number];

export const WORKFLOW_STATES = ["draft", "live", "paused"] as const;
export type WorkflowState = (typeof WORKFLOW_STATES)[number];

/** Steps that act behind the scenes (phase 1). */
export const WORKFLOW_RULE_ACTION_TYPES = ["send_webhook", "add_tags", "remove_tags", "assign", "set_status", "add_note", "update_lead", "notify_staff"] as const;
/** Behind-the-scenes steps; phase 3 adds SLA, priority, email and attributes, phase 4 "Run a workflow" (Apply rules keeps the phase 1 set). */
export const WORKFLOW_ACTION_TYPES = [
  ...WORKFLOW_RULE_ACTION_TYPES,
  "apply_sla",
  "set_priority",
  "email_visitor",
  "set_attribute",
  "run_workflow",
  "http_request",
  "snooze",
  "set_language",
  "add_summary_note",
  // Phase 6 (DEC-099): alerts, routing and webhook delivery. The menu offers each only where it works (`systemStepOffered` in the console).
  ...WORKFLOW_P6_STEP_TYPES,
  // Service desk (M27): the eleven background ticket steps. The menu offers them in every workflow; the server checks that the run has a ticket.
  ...SERVICE_BACKGROUND_STEP_TYPES,
  // After-sales (M27 S3) and orders (M27 S2): find an asset, propose a visit, search the catalogue, read an order.
  ...AFTER_SALES_STEP_TYPES,
  ...COMMERCE_BACKGROUND_STEP_TYPES,
] as const;
export type WorkflowActionType = (typeof WORKFLOW_ACTION_TYPES)[number];
export type WorkflowRuleActionType = (typeof WORKFLOW_RULE_ACTION_TYPES)[number];
export const WORKFLOW_FLOW_TYPES = ["branch", "apply_rules", "wait", "wait_for_webhook", "go_to_path", "end"] as const;
export type WorkflowFlowType = (typeof WORKFLOW_FLOW_TYPES)[number];
/** Steps the visitor sees or answers (customer-facing workflows only). */
export const WORKFLOW_FACING_TYPES = [
  "send_message",
  "reply_buttons",
  "collect_data",
  "collect_reply",
  "let_assistant_answer",
  "show_booking",
  "show_lead_form",
  "hand_to_person",
  "expected_reply_time",
  "ask_rating",
  "disable_reply",
  "enable_reply",
  "show_article",
  "verify_email",
  // Service desk (M27): the assistant takes the concern, the static ticket form, the client's open requests.
  ...SERVICE_FACING_STEP_TYPES,
  // Orders (M27 S2): the products, the review of the cart with its Confirm button, the client's orders.
  ...COMMERCE_FACING_STEP_TYPES,
] as const;
export type WorkflowFacingType = (typeof WORKFLOW_FACING_TYPES)[number];
export type WorkflowStepType = WorkflowActionType | WorkflowFlowType | WorkflowFacingType;
const FACING_TYPE_SET: ReadonlySet<string> = new Set(WORKFLOW_FACING_TYPES);
export const isFacingType = (type: string): type is WorkflowFacingType => FACING_TYPE_SET.has(type);
/**
 * Steps a run waits at (backend `WAITING_STEPS`): the visitor answers, the assistant answers, a card finishes or the clock runs out.
 * A path can loop back to itself only through one of them.
 */
export const WORKFLOW_WAITING_STEP_TYPES: ReadonlySet<WorkflowStepType> = new Set([
  "reply_buttons",
  "collect_data",
  "let_assistant_answer",
  "show_booking",
  "show_lead_form",
  "ask_rating",
  "wait",
  "wait_for_webhook",
  "collect_reply",
  "snooze",
  "verify_email",
  // Service desk (M27): the intake and the form wait for the client (backend `WAITING_STEPS`).
  "ticket_intake",
  "show_ticket_form",
  // Orders (M27 S2): the product card and the review wait for the client's tap.
  "show_catalogue",
  "create_order",
]);
/** Steps that need a conversation (backend `CONVERSATION_STEPS`). */
export const CONVERSATION_STEP_TYPES: ReadonlySet<WorkflowStepType> = new Set(["add_tags", "remove_tags", "assign", "set_status", "add_note", "apply_sla", "set_priority", "snooze", "set_language", "add_summary_note"]);

export const CONDITION_OPERATORS = ["is", "is_not", "is_any_of", "is_none_of", "contains", "not_contains", "contains_any_of", "starts_with", "is_set", "is_not_set", "gt", "lt"] as const;
export type ConditionOperator = (typeof CONDITION_OPERATORS)[number];
/** Operators that take no value. */
export const VALUELESS_OPERATORS: ReadonlySet<ConditionOperator> = new Set(["is_set", "is_not_set"]);
/** Operators whose value is a list (one item per line in the editor). */
export const LIST_OPERATORS: ReadonlySet<ConditionOperator> = new Set(["is_any_of", "is_none_of", "contains_any_of"]);

export type ConditionFieldKind = "text" | "list" | "number" | "bool";
/** Condition fields and the kind of value they hold (backend `CONDITION_FIELDS`). */
export const CONDITION_FIELDS: Readonly<Record<string, ConditionFieldKind>> = {
  "conversation.channel": "text",
  "conversation.agent_id": "text",
  "conversation.status": "text",
  "conversation.tags": "list",
  "conversation.language": "text",
  "conversation.topic": "text",
  "conversation.assigned": "bool",
  "conversation.team_id": "text",
  "conversation.message_count": "number",
  "message.text": "text",
  "message.role": "text",
  "lead.stage": "text",
  "lead.interest": "text",
  "lead.source": "text",
  "lead.has_owner": "bool",
  "booking.service": "text",
  "booking.status": "text",
  "visitor.email_known": "bool",
  "visitor.verified": "bool",
  "time.office_hours": "bool",
  "time.weekday": "text",
  "time.hour": "number",
  "event.reason": "text",
  "intent.topic": "text",
  "intent.action": "text",
  "conversation.priority": "text",
  "conversation.rating": "number",
  "event.name": "text",
  "page.url": "text",
  "sla.breached": "bool",
  "event.changed_by": "text",
  "event.attribute": "text",
  "event.previous": "text",
  "event.value": "text",
  "event.actor_user_id": "text",
  "event.changed": "list",
  "source.notice": "text",
  "source.run_kind": "text",
  "office.open": "bool",
  // Connectors (M27 SC, CONN-033): the event an inbound hook delivered, and the hook it came by
  "connector.event": "text",
  "connector.id": "text",
  "connector.hook": "text",
  // The customer the verified session proved (M29 PDI-006): tier, standing, who vouched, as of
  ...CUSTOMER_CONDITION_FIELDS,
  // Orders and quotes (M27 S2, scope 14.2): the order or quote a commerce run is about
  "order.status": "text",
  "order.total_php": "number",
  "order.approval_state": "text",
  "order.kind": "text",
  "order.connector_id": "text",
  "order.line_count": "number",
  "order.has_backorder": "bool",
  "quote.status": "text",
  "quote.source": "text",
  "quote.total_php": "number",
  "quote.line_count": "number",
  "quote.expired": "bool",
  "event.final": "bool",
  "event.side": "text",
  "ticket.order_number": "text",
  "ticket.order_status": "text",
  "ticket.order_items": "list",
  // Service desk (M27, scope 14.2): ticket, organisation, contact and the event's own fields
  "ticket.number": "text",
  "ticket.type": "text",
  "ticket.category": "text",
  "ticket.status": "text",
  "ticket.status_category": "text",
  "ticket.priority": "text",
  "ticket.channel": "text",
  "ticket.team_id": "text",
  "ticket.assignee": "text",
  "ticket.assignee_id": "text",
  "ticket.tags": "list",
  "ticket.organisation_id": "text",
  "ticket.org_tier": "text",
  "ticket.site_region": "text",
  "ticket.asset_category": "text",
  "ticket.has_asset": "bool",
  "ticket.contract_active": "bool",
  "ticket.warranty_status": "text",
  "ticket.sla_policy": "text",
  "ticket.sla.first_response_state": "text",
  "ticket.sla.resolution_state": "text",
  "ticket.reopen_count": "number",
  "ticket.requester_verified": "bool",
  "ticket.language": "text",
  "ticket.origin": "text",
  "ticket.created_in_office_hours": "bool",
  "ticket.sentiment": "text",
  "organisation.id": "text",
  "organisation.tier": "text",
  "organisation.industry": "text",
  "organisation.status": "text",
  "organisation.tags": "list",
  "contact.org_role": "text",
  "contact.can_order": "bool",
  "contact.verified": "bool",
  "contact.verified_via": "text",
  "contact.language": "text",
  "event.from": "text",
  "event.to": "text",
  "event.score": "number",
  "event.low": "bool",
  "event.outcome": "text",
  "event.metric": "text",
  "event.n": "number",
  "event.mentions": "list",
};

/** Objects a custom attribute can belong to; the condition field of each is `<object>.attr.<key>`. */
export const ATTRIBUTE_OBJECTS = ["visitor", "conversation", "lead"] as const;
export type AttributeObject = (typeof ATTRIBUTE_OBJECTS)[number];
export const ATTRIBUTE_TYPES = ["text", "number", "bool", "date", "list"] as const;
export type AttributeType = (typeof ATTRIBUTE_TYPES)[number];
export const ATTRIBUTE_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
export const ATTRIBUTE_LIMITS = { key: 40, label: 80, options: 50, option: 80, perWorkspace: 100 } as const;
const ATTRIBUTE_FIELD = /^(visitor|conversation|lead)\.attr\.([a-z][a-z0-9_]{0,39})$/;

export const attributeField = (object: AttributeObject, key: string): string => `${object}.attr.${key}`;

/** `visitor.attr.grade` gives `{object: "visitor", key: "grade"}`; `null` for any other field. */
export function parseAttributeField(field: string): { object: AttributeObject; key: string } | null {
  const match = ATTRIBUTE_FIELD.exec(field);
  const object = ATTRIBUTE_OBJECTS.find((candidate) => candidate === match?.[1]);
  const key = match?.[2];
  return object && key ? { object, key } : null;
}

/** What a condition on a custom attribute compares (dates compare as text, lists by membership). */
export const ATTRIBUTE_CONDITION_KIND: Readonly<Record<AttributeType, ConditionFieldKind>> = {
  text: "text",
  number: "number",
  bool: "bool",
  date: "text",
  list: "list",
};

/** Variables a message, note or custom webhook body can use (`{{name}}`, `{{name | fallback}}`). */
export const WORKFLOW_VARIABLES = [
  "visitor.name",
  "visitor.email",
  "conversation.channel",
  "conversation.link",
  "conversation.title",
  "lead.id",
  "lead.stage",
  "lead.interest",
  "lead.link",
  "booking.service",
  "booking.start_local",
  "time.now",
] as const;

export const WORKFLOW_SCHEDULES = ["any", "office_hours", "outside_office_hours", "custom"] as const;
export type WorkflowSchedule = (typeof WORKFLOW_SCHEDULES)[number];
/** The custom weekly schedule needs this plan. */
export const CUSTOM_SCHEDULE_MIN_PLAN: PlanKey = "pro";
export const SCHEDULE_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type ScheduleDay = (typeof SCHEDULE_DAYS)[number];
export const SCHEDULE_LIMITS = { rangesPerDay: 4, defaultFrom: "08:00", defaultTo: "17:00" } as const;
export const WORKFLOW_CHANNELS = ["widget", "webapp", "mobile", "messenger", "email", "api"] as const;
export const WORKFLOW_STATUSES = ["open", "waiting", "resolved", "closed"] as const;
export const NOTIFY_TARGETS = ["owners_admins", "assignee", "lead_owner", "team", "user"] as const;

/** Live workflows a plan allows (backend `LIVE_WORKFLOWS_*`); a plan missing here has no limit. */
export const LIVE_WORKFLOW_LIMITS: Readonly<Partial<Record<PlanKey, number>>> = { standard: 10, pro: 50 };

/** A step's own name: letters, numbers, spaces and `_` (backend `STEP_NAME_PATTERN`). */
export const WORKFLOW_STEP_NAME_PATTERN = /^[A-Za-z0-9_ ]+$/;

/** Limits (backend `app/core/constants/workflows.py`). */
export const WORKFLOW_LIMITS = {
  name: 120,
  description: 500,
  paths: 26,
  stepsPerPath: 30,
  branches: 20,
  conditionsPerGroup: 10,
  conditionGroups: 10,
  tags: 10,
  tag: 40,
  note: 2000,
  notifyTitle: 120,
  notifyBody: 500,
  webhookTemplate: 16 * 1024,
  valuesPerCondition: 50,
  buttonsMin: 2,
  buttonsMax: 8,
  buttonLabel: 40,
  messengerLabel: 20,
  message: 2000,
  guidance: 500,
  fieldLabel: 80,
  dropdownOptionsMin: 2,
  fieldOptions: 20,
  handReason: 200,
  customKey: 40,
  emailSubject: 150,
  emailBody: 5000,
  ratingText: 500,
  ratingComment: 500,
  attributeValue: 500,
  urlRules: 10,
  urlRuleValue: 500,
  secondsOnPageMax: 3600,
  unresponsiveMinutesMax: 10080,
} as const;

/** Wait step (`duration: {value, unit}`); caps by plan: Standard up to 24 hours, Pro and Institution up to 30 days. */
export const WAIT_UNITS = ["minutes", "hours", "days"] as const;
export type WaitUnit = (typeof WAIT_UNITS)[number];
export const WAIT_INTERRUPTS = ["visitor_reply", "staff_reply", "closed"] as const;
export type WaitInterrupt = (typeof WAIT_INTERRUPTS)[number];
export const WAIT_UNIT_MINUTES: Readonly<Record<WaitUnit, number>> = { minutes: 1, hours: 60, days: 1440 };
const WAIT_DAY_CAP_MINUTES = 30 * 1440;
export const WAIT_MAX_MINUTES: Readonly<Record<PlanKey, number>> = { starter: 1440, standard: 1440, pro: WAIT_DAY_CAP_MINUTES, institution: WAIT_DAY_CAP_MINUTES };
/** A booking card or lead form the visitor leaves alone closes after this long unless the step says otherwise (mirrors backend `CARD_TIMEOUT_MINUTES`). */
export const CARD_TIMEOUT_MINUTES = 30;

/** Conversation priorities (`set_priority`, Inbox sort and filter), lowest first. */
export const CONVERSATION_PRIORITIES = ["normal", "high", "urgent"] as const;
export type ConversationPriority = (typeof CONVERSATION_PRIORITIES)[number];
export const isConversationPriority = (value: string): value is ConversationPriority => CONVERSATION_PRIORITIES.some((priority) => priority === value);

/** Ask-for-a-rating step: five stars or thumbs, with an optional comment. */
export const RATING_SCALES = ["stars", "thumbs"] as const;
export type RatingScale = (typeof RATING_SCALES)[number];
export const RATING_STARS = 5;

/** Trigger options (`settings.trigger_options`). */
export const PAGE_URL_OPERATORS = ["is", "contains", "starts_with"] as const;
export type PageUrlOperator = (typeof PAGE_URL_OPERATORS)[number];
/** How long a page-visit message keeps showing: until the visitor engages, interacts at all, once, or every session. */
export const PAGE_FREQUENCIES = ["until_engaged", "until_interacted", "once", "every_session"] as const;
export type PageFrequency = (typeof PAGE_FREQUENCIES)[number];
export const UNRESPONSIVE_MINUTES_DEFAULT = 10;
export const PAGE_SECONDS_DEFAULT = 15;
/** Messenger shows reply buttons as quick replies: at most this many, labels cut to `messengerLabel`. */
export const MESSENGER_QUICK_REPLIES_MAX = 13;

export const COLLECT_FIELD_TYPES = ["text", "email", "phone", "number", "date", "dropdown"] as const;
export type CollectFieldType = (typeof COLLECT_FIELD_TYPES)[number];
/** Where a collected value is saved: a visitor field, a lead field (`lead.<key>`) or this run only (`var.<key>`). */
export const COLLECT_SAVE_TARGETS = ["visitor.name", "visitor.email", "visitor.phone", "lead", "var"] as const;
export type CollectSaveTarget = (typeof COLLECT_SAVE_TARGETS)[number];
export const HAND_TO_TARGETS = ["default", "team", "user"] as const;
export type HandToTarget = (typeof HAND_TO_TARGETS)[number];

/** What the chat gate can read from a visitor's message before the assistant answers (`intent.action`). */
export const WORKFLOW_INTENT_ACTIONS = ["book_tour", "lead"] as const;
export type WorkflowIntentAction = (typeof WORKFLOW_INTENT_ACTIONS)[number];

/** `ActionOut.kind` of the actions a booking card and a lead form can show. */
export const WORKFLOW_BOOKING_ACTION_KIND = "booking";
export const WORKFLOW_LEAD_ACTION_KIND = "capture_lead";

export const WORKFLOW_FIRST_PATH = "A";
/** `details.problems` of a refused save: one problem per line. */
export const WORKFLOW_PROBLEM_SEPARATOR = "\n";

/** SLA policies (Settings, Institution): name length, the longest target in minutes (365 days) and how many a workspace may keep. */
export const SLA_LIMITS = { name: 80, minutesMax: 365 * 1440, policies: 50, warnDefault: 10, firstResponseDefault: 15 } as const;
